import CustomButton from '@/components/atoms/CustomButton';
import { Box, Center, HStack, Image, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import UserProfileBox, { USER_PROFILE_CARD_HEIGHT } from './UserProfileBox';
import FilePicker from '@/components/atoms/FilePicker';
import svgFiles from '@/assets/svg/svgFiles';
import { MAX_IMAGE_SIZE } from '@/constants/stringVars';

interface UserProfilePhotoBoxProps {
  updateProfilePhoto: (photo: File) => Promise<boolean>;
  initialPreviewUrl?: string;
}

const UserProfilePhotoBox = ({ updateProfilePhoto, initialPreviewUrl = '' }: UserProfilePhotoBoxProps) => {
  const inputProfilePhotoRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(initialPreviewUrl);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const hasInitImagePreview = !!preview || !!initialPreviewUrl;
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const uploadPhotoWithLoading = useCallback(async (file: File | null) => {
    if (!file) return;

    setPreview('');
    setIsUploadingPhoto(true);

    await updateProfilePhoto(file);
    const previewUrl = URL.createObjectURL(file);

    setIsUploadingPhoto(false);
    setPreview(previewUrl);
  }, []);

  return (
    <UserProfileBox
      style={{ minHeight: USER_PROFILE_CARD_HEIGHT, height: 'auto', display: 'flex', alignItems: 'center' }}
    >
      <Center width="100%">
        <HStack justifyContent={'space-between'} width={'100%'} flexWrap={'wrap'} gap={'20px'}>
          <Box maxWidth={'180px'}>
            <Text fontSize={'26px'} fontWeight={'bold'} marginBottom={'10px'} color={'text.mediumBlue'}>
              Profile Photo
            </Text>
            <Text variant={'urbanistSmall'} color={'text.mediumBlue'}>
              File details: not smaller than 100x100px and not larger than {MAX_IMAGE_SIZE}.
            </Text>
            <Box marginTop={'16px'}>
              <CustomButton
                onClick={() => inputProfilePhotoRef.current?.click()}
                label={isUploadingPhoto ? 'Uploading...' : 'Upload'}
                isLoading={isUploadingPhoto}
                backgroundColor="primary.500"
                hoverBackgroundColor="primary.600"
                labelHoverColor="white"
                labelColor="white"
                borderHoverColor="transparent"
              />
            </Box>
            <FilePicker
              inputRef={inputProfilePhotoRef}
              selectedFile={null}
              setSelectedFile={uploadPhotoWithLoading}
              accept="image/*"
            />
          </Box>
          <Center
            width={190}
            height={190}
            borderRadius={'50%'}
            backgroundImage={preview ?? ''}
            backgroundPosition={'center'}
            backgroundRepeat={'no-repeat'}
            backgroundSize={'cover'}
            borderWidth={5}
            borderColor={'white'}
            boxShadow={'0px 12px 30px 0px #0E00621A'}
          >
            {!hasInitImagePreview && <Image alt="generic-profile" src={svgFiles.genericUser} height={80} width={80} />}
          </Center>
        </HStack>
      </Center>
    </UserProfileBox>
  );
};

export default UserProfilePhotoBox;
